import React, { useState } from 'react';
import { useNotifications, Notification } from '../../providers/NotificationProvider/NotificationContext';
import { X, CheckCircle, XCircle, AlertTriangle, Info, Trash2, Clipboard, ChevronDown, Hash } from 'lucide-react';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const INITIAL_ITEMS = 10;
const LOAD_MORE_COUNT = 10;

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
  const { notifications, unreadCount, markAsRead, removeNotification, clearAll } = useNotifications();
  const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Reset visible count when notifications change
  React.useEffect(() => {
    setVisibleCount(INITIAL_ITEMS);
  }, [notifications.length]);

  const markAllAsRead = () => {
    notifications.forEach((n) => {
      if (!n.read) markAsRead(n.id);
    });
  };

  const handleCopyCorrelationId = async (correlationId: string, notificationId: string) => {
    await navigator.clipboard.writeText(correlationId);
    setCopiedId(notificationId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + LOAD_MORE_COUNT);
  };

  const sortedNotifications = [...notifications].reverse(); // Newest first
  const visibleNotifications = sortedNotifications.slice(0, visibleCount);
  const hasMore = visibleCount < sortedNotifications.length;

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex justify-end">
      {/* Panel */}
      <div className="w-full sm:w-96 bg-[#1e1e1e] h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Notifications</h2>
            {unreadCount > 0 && <span className="text-xs text-gray-400">{unreadCount} unread</span>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded transition">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {visibleNotifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Info className="w-12 h-12 mx-auto mb-2 text-gray-600" />
              <p className="text-gray-400">No notifications yet</p>
            </div>
          ) : (
            <div>
              {visibleNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 border-b border-gray-800 hover:bg-gray-800/50 transition cursor-pointer ${
                    !notification.read ? 'bg-gray-800/30 border-l-2 border-l-red-600' : ''
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">{getIcon(notification.type)}</div>
                    <div className="flex-1 min-w-0">
                      {notification.title && <p className="font-semibold text-gray-100 text-sm">{notification.title}</p>}
                      <p className="text-gray-300 text-sm line-clamp-2">{notification.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatTime(notification.timestamp)}</p>

                      {/* Actions row with red separator lines - only show if there are actions */}
                      {(notification.meta?.downloadUrl || notification.correlationId) && (
                        <div className="mt-3 flex items-center justify-center gap-0">
                          {/* Download button (if available) */}
                          {notification.meta?.downloadUrl && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                  window.dispatchEvent(
                                    new CustomEvent('performDownload', { detail: { url: notification.meta.downloadUrl } }),
                                  );
                                }}
                                className="flex-1 px-3 py-2 text-white transition text-xs font-medium text-center hover:text-gray-100"
                                title="Download report"
                              >
                                Download
                              </button>
                              {notification.correlationId && <div className="w-px h-6 bg-red-500"></div>}
                            </>
                          )}

                          {/* Copy correlation id (if present) */}
                          {notification.correlationId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyCorrelationId(notification.correlationId!, notification.id);
                              }}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-gray-200 transition text-xs font-medium hover:text-white"
                              title="Copy correlation ID"
                            >
                              <Clipboard className={`w-3.5 h-3.5 ${copiedId === notification.id ? 'text-green-400' : 'text-gray-200'}`} />
                              <span>{copiedId === notification.id ? 'Copied!' : 'Copy ID'}</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNotification(notification.id);
                      }}
                      className="flex-shrink-0 p-1 hover:bg-gray-700 rounded transition"
                    >
                      <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Show More Button */}
              {hasMore && (
                <div className="p-4 flex justify-center">
                  <button
                    onClick={handleShowMore}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition text-sm font-medium"
                  >
                    <span>Show More</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-4 border-t border-gray-700 flex gap-2">
            <button
              onClick={markAllAsRead}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition text-sm font-medium"
            >
              Mark all as read
            </button>
            <button
              onClick={clearAll}
              className="flex-1 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded transition text-sm font-medium border border-red-600/30"
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
